import { HttpStatusCode } from "axios";

import { LANG_DEFAULT, LANG_HEADER_NAME } from "../constants";
import { refreshAccessToken } from "../helpers/auth/token-refresh.helpers";
import { apiBaseUrl } from "../helpers/env.helpers";
import { Log } from "../helpers/log.helpers";
import { getLangLocalStorage } from "../helpers/rtl.helpers";
import { normalizeUrlBasePath } from "../helpers/url.helpers";
import type { ICustomTranslation, SupportedLanguage } from "../models";
import { apiGetAll } from "./generic-api";

export type TranslationBundle = Record<string, unknown>;

/**
 * Fetches a translation bundle with a plain request, bypassing the company API
 * envelope check (`{ status, data }`). The i18n endpoints return the bare
 * translation tree — the same payload i18next consumes — so the generic API
 * wrappers would reject it as an invalid response.
 */
const fetchRawBundle = async (path: string): Promise<TranslationBundle> => {
  const url = `${normalizeUrlBasePath(apiBaseUrl() || "")}${path}`;
  // The full catalogue is served only to a session.
  const init: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      [LANG_HEADER_NAME]: getLangLocalStorage() || LANG_DEFAULT,
    },
  };

  try {
    const firstResponse = await fetch(url, init);
    // An expired access token gets one refresh and retry, as axios calls do.
    const response =
      firstResponse.status === HttpStatusCode.Unauthorized
        ? await refreshAccessToken().then(() => fetch(url, init))
        : firstResponse;

    if (!response.ok) {
      Log.warn(`Failed to fetch translation bundle: ${path}`, response.status);

      return {};
    }

    return (await response.json()) as TranslationBundle;
  } catch (error) {
    Log.warn(`Error fetching translation bundle: ${path}`, error);

    return {};
  }
};

export class I18nApi {
  static async getCustomTranslations(
    companyId: number,
  ): Promise<ICustomTranslation[]> {
    const response = await apiGetAll<ICustomTranslation>(
      `/api/v1/core/i18n/c/${companyId}/custom-translations`,
      {},
    );
    return response ?? [];
  }

  /**
   * Fetches the raw core translation bundle for a language straight from the
   * backend, before any client-side custom-translation overrides are injected.
   * Used to show the true original value of a key.
   */
  static async getTranslationsBundle(
    lang: SupportedLanguage,
  ): Promise<TranslationBundle> {
    return fetchRawBundle(`/api/v1/core/i18n/${lang}`);
  }
}
